import type { CategoryName, ConsentAction, ConsentState } from './types';

/** Decide what the visitor did from the categories offered and the ones accepted. */
export function consentAction(offered: readonly CategoryName[], accepted: readonly string[]): ConsentAction {
  const optional = offered.filter((c) => c !== 'necessary');
  const acceptedOptional = optional.filter((c) => accepted.includes(c));
  if (optional.length > 0 && acceptedOptional.length === 0) return 'reject_all';
  if (acceptedOptional.length === optional.length) return 'accept_all';
  return 'custom';
}

export interface CookieLike {
  consentId: string;
  revision: number;
  categories: string[];
  languageCode: string;
  lastConsentTimestamp: string;
}

export function toState(offered: readonly CategoryName[], cookie: CookieLike): ConsentState {
  const accepted = offered.filter((c) => c === 'necessary' || cookie.categories.includes(c));
  const rejected = offered.filter((c) => !accepted.includes(c));
  return {
    consentId: cookie.consentId,
    revision: cookie.revision,
    accepted,
    rejected,
    action: consentAction(offered, accepted),
    language: cookie.languageCode,
    timestamp: cookie.lastConsentTimestamp,
  };
}

/**
 * Categories stored in the consent cookie, read directly (before the engine starts) so auto-blocking can let
 * already-accepted trackers through on page load. Returns null without a valid cookie for this revision.
 */
export function readStoredCategories(cookieName: string, revision: number, cookieString = document.cookie): string[] | null {
  const raw = cookieString
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(`${cookieName}=`));
  if (!raw) return null;
  try {
    const value = JSON.parse(decodeURIComponent(raw.slice(cookieName.length + 1))) as { categories?: unknown; revision?: unknown };
    if (!Array.isArray(value.categories) || value.revision !== revision) return null;
    return value.categories.filter((c): c is string => typeof c === 'string');
  } catch {
    return null;
  }
}
